'use client'
import React, { useEffect, useRef, useCallback, useMemo } from 'react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { getMapLanguage, getMapRegion } from '@utils'
import 'leaflet/dist/leaflet.css'

const shortenPrice = (raw, format) => {
  const n = parseFloat(String(raw ?? '').replace(/[^0-9.]/g, ''))
  if (isNaN(n)) return String(raw ?? '')
  if (format === 'zh') {
    if (n >= 100000000) return (n / 100000000).toFixed(1).replace(/\.0$/, '') + '亿'
    if (n >= 10000)     return (n / 10000).toFixed(1).replace(/\.0$/, '') + '万'
    if (n >= 1000)      return (n / 1000).toFixed(1).replace(/\.0$/, '') + '千'
    return String(n)
  }
  if (format === 'none') return n.toLocaleString()
  if (n >= 1000000000) return (n / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B'
  if (n >= 1000000)    return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (n >= 1000)       return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return String(n)
}

const langToShortenFormat = (lang) => {
  if (!lang) return 'en'
  const l = lang.toLowerCase()
  if (l.startsWith('zh')) return 'zh'
  return 'en'
}

const MARKER_PRESETS = [
  { value: 'pin',    label: '📍 Pin',         svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40"><path d="M16 0C7.163 0 0 7.163 0 16c0 10 16 24 16 24s16-14 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/><circle cx="16" cy="16" r="6" fill="white"/></svg>` },
  { value: 'circle', label: '⬤ Circle',       svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="12" fill="${color}" stroke="white" stroke-width="2.5"/></svg>` },
  { value: 'square', label: '■ Square',        svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><rect x="2" y="2" width="24" height="24" rx="4" fill="${color}" stroke="white" stroke-width="2.5"/></svg>` },
  { value: 'star',   label: '★ Star',          svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 20,12 31,12 22,19 25,30 16,23 7,30 10,19 1,12 12,12" fill="${color}" stroke="white" stroke-width="1.5"/></svg>` },
  { value: 'home',   label: '🏠 Home',         svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 30,16 25,16 25,30 19,30 19,22 13,22 13,30 7,30 7,16 2,16" fill="${color}" stroke="white" stroke-width="1.5"/></svg>` },
  { value: 'price',  label: '💰 Price pill',   svg: (color, label, opts = {}) => {
    const text   = label || '•'
    const fs     = opts.fontSize   ?? 11
    const pad    = 8
    const ch     = fs * 0.68
    const W      = Math.max(text.length * ch, 28) + pad * 2
    const H      = fs + 10
    const tipH   = 6
    const totalH = H + tipH
    const midX   = W / 2
    const R      = opts.radius ?? H / 2
    const bg     = opts.bg     ?? color
    const tc     = opts.textColor ?? '#ffffff'
    const bw     = opts.borderWidth ?? 0
    const bc     = opts.borderColor ?? 'transparent'
    const border = bw > 0 ? `<rect x="${bw/2}" y="${bw/2}" width="${W - bw}" height="${H - bw}" rx="${R}" fill="none" stroke="${bc}" stroke-width="${bw}"/>` : ''
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${totalH}" viewBox="0 0 ${W} ${totalH}"><rect x="0" y="0" width="${W}" height="${H}" rx="${R}" fill="${bg}"/>${border}<polygon points="${midX - 5},${H} ${midX + 5},${H} ${midX},${totalH}" fill="${bg}"/><text x="${midX}" y="${H / 2 + fs * 0.37}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${fs}" font-weight="700" fill="${tc}">${text}</text></svg>`
  }},
]

const getMarkerSvg = (style, color, w, h, label, markerCfg) => {
  const preset   = MARKER_PRESETS.find(p => p.value === style) ?? MARKER_PRESETS[0]
  const pillOpts = style === 'price' ? {
    bg: markerCfg?.pillBg ?? color,
    textColor: markerCfg?.pillTextColor ?? '#ffffff',
    borderColor: markerCfg?.pillBorderColor ?? 'transparent',
    borderWidth: markerCfg?.pillBorderWidth ?? 0,
    radius: markerCfg?.pillRadius ?? undefined,
    fontSize: markerCfg?.pillFontSize ?? 11,
  } : {}
  let svg = preset.svg?.(color, label, pillOpts) ?? MARKER_PRESETS[0].svg(color)
  if (w && h && style !== 'price') {
    svg = svg.replace(/width="[^"]*"/, `width="${w}"`)
             .replace(/height="[^"]*"/, `height="${h}"`)
  }
  return svg
}

// ─── Leaflet ──────────────────────────────────────────────────────────────────
function LeafletMobileMap({ coordinates, hoveredProperty, latitude, longitude, mapConfig, onMarkerClick, lang, getTileLayer }) {
  const mapRef           = useRef(null)
  const leafletRef       = useRef(null)
  const LRef             = useRef(null)
  const markersRef       = useRef({})
  const coordinatesRef   = useRef([])
  const onMarkerClickRef = useRef(onMarkerClick)
  const mapConfigRef     = useRef(mapConfig)
  const langRef          = useRef(lang)
  useEffect(() => { onMarkerClickRef.current = onMarkerClick }, [onMarkerClick])
  useEffect(() => { mapConfigRef.current = mapConfig },         [mapConfig])
  useEffect(() => { langRef.current = lang },                   [lang])

  // makeIcon reads from refs — stable forever, markers never torn down on re-render
  const makeIcon = useCallback((L, highlighted, label) => {
    const cfg         = mapConfigRef.current
    const markerSize  = cfg?.marker?.sizeMobile       ?? 24
    const activeSize  = cfg?.markerActive?.sizeMobile ?? 30
    const markerStyle = cfg?.marker?.style            ?? 'pin'
    const markerColor = cfg?.marker?.color            ?? '#3b82f6'
    const activeStyle = cfg?.markerActive?.style      ?? 'pin'
    const activeColor = cfg?.markerActive?.color      ?? '#ef4444'
    const w     = highlighted ? activeSize  : markerSize
    const h     = Math.round(w * 1.25)
    const style = highlighted ? activeStyle : markerStyle
    const color = highlighted ? activeColor : markerColor
    const pCfg  = highlighted ? cfg?.markerActive : cfg?.marker
    const svg   = getMarkerSvg(style, color, w, h, label, pCfg)
    const html  = `<div style="line-height:0">${svg}</div>`
    if (style === 'price') {
      const wMatch = svg.match(/width="([^"]+)"/)
      const hMatch = svg.match(/height="([^"]+)"/)
      const sw = wMatch ? Math.ceil(parseFloat(wMatch[1])) : 60
      const sh = hMatch ? Math.ceil(parseFloat(hMatch[1])) : 28
      return L.divIcon({ className: '', html, iconSize: [sw, sh], iconAnchor: [sw / 2, sh] })
    }
    return L.divIcon({ className: '', html, iconSize: [w, h], iconAnchor: [w / 2, h] })
  }, [])

  const addMarkers = useCallback((L, map, coords) => {
    const incomingIds = new Set(coords.map(c => c._id))
    Object.keys(markersRef.current).forEach(id => {
      if (!incomingIds.has(id)) { markersRef.current[id].remove(); delete markersRef.current[id] }
    })
    coords.forEach(({ _id, latitude: lat, longitude: lng, salePrice, rentPrice }) => {
      if (markersRef.current[_id]) return
      const rawPrice   = salePrice || rentPrice || ''
      const fmt        = mapConfigRef.current?.marker?.priceShorten === false ? 'none' : langToShortenFormat(langRef.current)
      const priceLabel = rawPrice ? shortenPrice(rawPrice, fmt) : ''
      const marker = L.marker([Number(lat), Number(lng)], { icon: makeIcon(L, false, priceLabel) })
        .addTo(map)
        .on('click',    () => onMarkerClickRef.current?.({ _id, latitude: lat, longitude: lng }))
        .on('touchend', () => onMarkerClickRef.current?.({ _id, latitude: lat, longitude: lng }))
      markersRef.current[_id] = marker
    })
  }, [makeIcon])

  useEffect(() => {
    if (typeof window === 'undefined' || !mapRef.current) return
    if (mapRef.current._leaflet_id) return
    import('leaflet').then((mod) => {
      const L = mod.default ?? mod
      if (!mapRef.current || mapRef.current._leaflet_id) return
      LRef.current = L
      const map = L.map(mapRef.current, {
        center: [Number(latitude) || 13.732772, Number(longitude) || 100.5801953],
        zoom: mapConfigRef.current?.zoom ?? 15, zoomControl: false, scrollWheelZoom: false, dragging: true, tap: false,
      })
      const { url, options } = getTileLayer()
      L.tileLayer(url, options).addTo(map)
      leafletRef.current = map
      if (coordinatesRef.current.length > 0) addMarkers(L, map, coordinatesRef.current)
    })
    return () => {
      if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null }
      markersRef.current = {}
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    coordinatesRef.current = coordinates ?? []
    const map = leafletRef.current; const L = LRef.current
    if (!map || !L) return
    addMarkers(L, map, coordinatesRef.current)
  }, [coordinates, addMarkers])

  useEffect(() => {
    const map = leafletRef.current; const L = LRef.current
    if (!map || !L || !latitude || !longitude) return
    map.panTo([Number(latitude), Number(longitude)])
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const highlighted = id === hoveredProperty
      const coord       = coordinatesRef.current.find(c => c._id === id)
      const rawPrice    = coord?.salePrice || coord?.rentPrice || ''
      const fmt         = mapConfigRef.current?.marker?.priceShorten === false ? 'none' : langToShortenFormat(langRef.current)
      const priceLabel  = rawPrice ? shortenPrice(rawPrice, fmt) : ''
      marker.setIcon(makeIcon(L, highlighted, priceLabel))
      marker.setZIndexOffset(highlighted ? 1000 : 0)
    })
  }, [hoveredProperty, latitude, longitude, makeIcon])

  return <div ref={mapRef} style={{ width: '100%', height: '92vh', zIndex: 49 }} />
}

function OSMMobileMap(props) {
  return <LeafletMobileMap {...props} getTileLayer={() => ({
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: { attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>', maxZoom: 19 },
  })} />
}

function MapboxMobileMap(props) {
  const { mapboxToken, mapboxStyle = 'streets-v12' } = props.mapConfig ?? {}
  if (!mapboxToken) return <div style={{ width: '100%', height: '92vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 13 }}>Mapbox token not configured</div>
  return <LeafletMobileMap {...props} getTileLayer={() => ({
    url: `https://api.mapbox.com/styles/v1/mapbox/${mapboxStyle}/tiles/{z}/{x}/{y}?access_token=${mapboxToken}`,
    options: { tileSize: 512, zoomOffset: -1, maxZoom: 22, attribution: '© <a href="https://www.mapbox.com/">Mapbox</a>' },
  })} />
}

function AzureMobileMap(props) {
  const { azureToken, azureStyle = 'microsoft.base.road' } = props.mapConfig ?? {}
  if (!azureToken) return <div style={{ width: '100%', height: '92vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 13 }}>Azure Maps key not configured</div>
  return <LeafletMobileMap {...props} getTileLayer={() => ({
    url: `https://atlas.microsoft.com/map/tile?api-version=2.0&tilesetId=${azureStyle}&zoom={z}&x={x}&y={y}&subscription-key=${azureToken}`,
    options: { tileSize: 256, maxZoom: 22, attribution: '© <a href="https://azure.microsoft.com/en-us/products/azure-maps">Azure Maps</a>' },
  })} />
}

// ─── Google ───────────────────────────────────────────────────────────────────
function GoogleMobileMap({ coordinates, hoveredProperty, latitude, longitude, mapConfig, onMarkerClick, lang, zoomControl = false }) {
  const zoom              = mapConfig?.zoom ?? 15
  const mapRef            = useRef(null)
  const onMarkerClickRef  = useRef(onMarkerClick)
  useEffect(() => { onMarkerClickRef.current = onMarkerClick }, [onMarkerClick])

  useEffect(() => {
    const lat = Number(latitude), lng = Number(longitude)
    if (mapRef.current && isFinite(lat) && isFinite(lng) && lat !== 0 && lng !== 0)
      mapRef.current.panTo({ lat, lng })
  }, [latitude, longitude])

  const markerStyle = mapConfig?.marker?.style            ?? 'pin'
  const markerColor = mapConfig?.marker?.color            ?? '#3b82f6'
  const activeStyle = mapConfig?.markerActive?.style      ?? 'pin'
  const activeColor = mapConfig?.markerActive?.color      ?? '#ef4444'
  const mw = mapConfig?.marker?.sizeMobile       ?? 24; const mh = Math.round(mw * 1.25)
  const aw = mapConfig?.markerActive?.sizeMobile ?? 30; const ah = Math.round(aw * 1.25)

  const { isLoaded } = useJsApiLoader({
    id: 'script-loader',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API ?? '',
    language: getMapLanguage(lang),
    region:   getMapRegion(lang),
  })

  const makeIcon = (style, color, w, h, label, cfg) => ({
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(getMarkerSvg(style, color, w, h, label, cfg)),
  })

  const centerLat = Number(latitude)  || 13.732772
  const centerLng = Number(longitude) || 100.5801953
  const center = useMemo(() => ({ lat: centerLat, lng: centerLng }), [centerLat, centerLng])

  if (!isLoaded) return <div style={{ width: '100%', height: '92vh', background: '#f3f4f6' }} />

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '92vh', zIndex: 49 }}
      center={center}
      zoom={zoom}
      options={{ disableDefaultUI: true, zoomControl }}
      onLoad={map => { mapRef.current = map }}
    >
      {(coordinates ?? []).map(({ _id, latitude: lat, longitude: lng, salePrice, rentPrice }) => {
        const rawPrice   = salePrice || rentPrice || ''
        const fmt = mapConfig?.marker?.priceShorten === false ? 'none' : langToShortenFormat(lang)
        const priceLabel = rawPrice ? shortenPrice(rawPrice, fmt) : ''
        const isActive   = _id === hoveredProperty
        const style  = isActive ? activeStyle : markerStyle
        const color  = isActive ? activeColor : markerColor
        const w = isActive ? aw : mw
        const h = isActive ? ah : mh
        const cfg = isActive ? mapConfig?.markerActive : mapConfig?.marker
        return (
          <Marker
            key={_id}
            position={{ lat: Number(lat), lng: Number(lng) }}
            icon={makeIcon(style, color, w, h, priceLabel, cfg)}
            zIndex={isActive ? 100 : 40}
            onClick={() => onMarkerClickRef.current?.({ _id, latitude: lat, longitude: lng })}
          />
        )
      })}
    </GoogleMap>
  )
}

// ─── Export ───────────────────────────────────────────────────────────────────
const MobileSearchMap = ({ coordinates, hoveredProperty, latitude, longitude, lang = 'en', mapConfig, cardStyle, onMarkerClick, zoomControl = false, dictionary }) => {
  const provider    = mapConfig?.provider ?? 'google'
  const sharedProps = { coordinates, hoveredProperty, latitude, longitude, mapConfig, onMarkerClick, lang, zoomControl }

  if (provider === 'osm')    return <div className='z-[49]'><OSMMobileMap    {...sharedProps} /></div>
  if (provider === 'mapbox') return <div className='z-[49]'><MapboxMobileMap {...sharedProps} /></div>
  if (provider === 'azure')  return <div className='z-[49]'><AzureMobileMap  {...sharedProps} /></div>
  return <div className='z-[49]'><GoogleMobileMap {...sharedProps} /></div>
}

export default MobileSearchMap